/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import { useMachine, useService } from '@xstate/react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import CrowdSettingsMachine from './CrowdSettingsMachine';
import { Button, Checkbox, ContentBody, ExtJS, SettingsSection, Textfield } from 'nexus-ui-plugin';
import UIStrings from '../../../../constants/UIStrings';

export default function CrowdSettings() {
  const service = useMachine(CrowdSettingsMachine, {devTools: window.location.search === '?debug'})[2];
  return <CrowdSettingsForm service={service}/>;
}

export function CrowdSettingsForm({service}) {
  const [current, send] = useService(service);

  const context = current.context;
  const isLoading = current.matches('loading') || current.matches('saving');
  const settings = context.data;
  const isPristine = context.isPristine;
  const isInvalid = context.isInvalid;
  const isCrowdServerUrlValid = context.isCrowdServerUrlValid;

  function updateSettings({target}) {
    const value = (function () {
      if (target.type === 'checkbox') {
        return target.checked;
      }
      else {
        return target.value ? target.value : '';
      }
    })();
    const name = target.name || target.id;
    send('UPDATE', {
      data: {
        ...settings,
        [name]: value
      }
    });
  }

  function saveSettings(event) {
    event.preventDefault();
    send('SAVE');
  }

  function discardChanges() {
    send('DISCARD');
  }

  function verifyConnection() {
    send('VERIFY-CONNECTION');
  }

  function clearCache() {
    send('CLEAR-CACHE');
  }

  function handleEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      send('SAVE');
    }
  }

  ExtJS.setDirtyStatus('CrowdSettings', !isPristine);

  return <ContentBody className='nxrm-crowd-settings'>
    <SettingsSection isLoading={isLoading}>
      <SettingsSection.FieldWrapper
          labelText={UIStrings.CROWD_SETTINGS.FIELDS.enabledLabel}
      >
        <Checkbox
            checkboxId='enabled'
            isChecked={settings.enabled}
            onChange={updateSettings}
            onKeyPress={handleEnter}
        >
          {UIStrings.CROWD_SETTINGS.FIELDS.enabledDescription}
        </Checkbox>
      </SettingsSection.FieldWrapper>
      <SettingsSection.FieldWrapper
          labelText={UIStrings.CROWD_SETTINGS.FIELDS.realmActiveLabel}
      >
        <Checkbox
            checkboxId='realmActive'
            isChecked={settings.realmActive}
            onChange={updateSettings}
            onKeyPress={handleEnter}
        >
          <span dangerouslySetInnerHTML={UIStrings.CROWD_SETTINGS.FIELDS.realmActiveDescription} />
        </Checkbox>
      </SettingsSection.FieldWrapper>
      <SettingsSection.FieldWrapper
          labelText={UIStrings.CROWD_SETTINGS.FIELDS.urlLabel}
          descriptionText={UIStrings.CROWD_SETTINGS.FIELDS.urlDescription}
      >
        <Textfield
            isRequired
            name="url"
            value={settings.url}
            onChange={updateSettings}
            onKeyPress={handleEnter}
            isValid={isCrowdServerUrlValid}
            validityMessage={UIStrings.CROWD_SETTINGS.FIELDS.urlValidationError}
        />
      </SettingsSection.FieldWrapper>
      {settings.url.startsWith('https') ?
      <SettingsSection.FieldWrapper
          labelText={UIStrings.CROWD_SETTINGS.FIELDS.useTrustStoreLabel}
      >
        <Checkbox
            checkboxId='useTrustStoreForUrl'
            isChecked={settings.useTrustStoreForUrl}
            onChange={updateSettings}
            onKeyPress={handleEnter}
        >
          {UIStrings.CROWD_SETTINGS.FIELDS.useTrustStoreDescription}
          <br />
          <a href="#admin/security/sslcertificates">Configure the NXRM truststore</a>
        </Checkbox>
      </SettingsSection.FieldWrapper>
      : null}
      <SettingsSection.FieldWrapper
          labelText={UIStrings.CROWD_SETTINGS.FIELDS.applicationNameLabel}
      >
        <Textfield
            isRequired
            name="applicationName"
            autoComplete="off"
            value={settings.applicationName}
            onChange={updateSettings}
            onKeyPress={handleEnter}
        />
      </SettingsSection.FieldWrapper>
      <SettingsSection.FieldWrapper
          labelText={UIStrings.CROWD_SETTINGS.FIELDS.applicationPasswordLabel}
      >
        <Textfield
            isRequired
            name="applicationPassword"
            type="password"
            autoComplete="off"
            value={settings.applicationPassword}
            onChange={updateSettings}
            onKeyPress={handleEnter}
        />
      </SettingsSection.FieldWrapper>
      <SettingsSection.FieldWrapper
          labelText={UIStrings.CROWD_SETTINGS.FIELDS.timeoutLabel}
          descriptionText={UIStrings.CROWD_SETTINGS.FIELDS.timeoutDescription}
      >
        <Textfield
            name="timeout"
            type="number"
            min="0"
            value={settings.timeout ? settings.timeout : ''}
            onChange={updateSettings}
            onKeyPress={handleEnter}
        />
      </SettingsSection.FieldWrapper>

      <SettingsSection.Footer>
        <Button variant="primary" disabled={isPristine || isInvalid} onClick={saveSettings} type="submit">
          {UIStrings.SETTINGS.SAVE_BUTTON_LABEL}
        </Button>
        <Button disabled={isPristine} onClick={discardChanges}>
          {UIStrings.SETTINGS.DISCARD_BUTTON_LABEL}
        </Button>
        <Button disabled={isInvalid} onClick={verifyConnection}>
          {UIStrings.CROWD_SETTINGS.BUTTONS.VERIFY_BUTTON_LABEL}
        </Button>
        <Button onClick={clearCache}>
          <FontAwesomeIcon icon={faTrash} pull="left"/>
          {UIStrings.CROWD_SETTINGS.BUTTONS.CLEAR_BUTTON_LABEL}
        </Button>
      </SettingsSection.Footer>
    </SettingsSection>
  </ContentBody>;
}
